import { useLoadingLegacy } from '@zc-react-admin/hooks';

import { getIsLogin, selectUserInfo, setUserInfo } from '@/features/auth/authStore';
import { router, usePreviousRoute, useRouter } from '@/features/router';
import { PlatformAccountService, PlatformLoginService } from '@/service/api/platform/index';
import type { LoginFormVo } from '@/service/api/platform/login';
import { store } from '@/store';
import { localStg } from '@/utils/storage';

import { initAuthRoutes } from '../router/initRouter';
import { setIsAlreadyPatch } from '../router/routeStore';
import { useCacheTabs } from '../tab/tabHooks';

import { resetAuth as resetAuthAction, setToken } from './authStore';
import { clearAuthStorage } from './shared';

export const getUserInfo = async () => {
  const { data: userInfo } = await PlatformAccountService.getInfo();
  userInfo!.roles = [userInfo!.roles as never as string];
  localStg.set('userInfo', userInfo!);
  store.dispatch(setUserInfo(userInfo!));
};

export function useAuth() {
  const userInfo = useAppSelector(selectUserInfo);

  const isLogin = useAppSelector(getIsLogin);

  function hasAuth(codes: string | string[]) {
    if (!isLogin) {
      return false;
    }

    if (typeof codes === 'string') {
      return userInfo.buttons.includes(codes);
    }

    return codes.some(code => userInfo.buttons.includes(code));
  }

  return {
    hasAuth
  };
}

export function useInitAuth() {
  const { endLoading, loading, startLoading } = useLoadingLegacy();

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { navigate } = useRouter();

  // const redirectUrl = searchParams.get('redirect');

  async function toLogin(loginFormVo: LoginFormVo, redirect = true) {
    if (loading) return;

    startLoading();
    try {
      const { data: loginData } = await PlatformLoginService.login(loginFormVo);
      if (loginData) {
        localStg.set('token', loginData.token);
        localStg.set('refreshToken', loginData.refreshToken);

        // 2. store user info
        dispatch(setToken(loginData.token));
        dispatch(setIsAlreadyPatch(true));
        await initAuthRoutes(router.reactRouter.patchRoutes);
        if (redirect) {
          // if (redirectUrl) {
          //   navigate(redirectUrl);
          // } else {
          //   navigate('/');
          // }
          navigate('/');
        }
        window.$notification?.success({
          // description: t('page.login.common.welcomeBack', { userName: userInfo!.realName }),
          message: t('page.login.common.loginSuccess')
        });
      }
    } catch {
      endLoading();
    }
  }

  return {
    loading,
    toLogin
  };
}

export function useResetAuth() {
  const dispatch = useAppDispatch();

  const previousRoute = usePreviousRoute();

  const cacheTabs = useCacheTabs();

  const { navigate, push, resetRoutes } = useRouter();

  function resetAuth() {
    clearAuthStorage();

    dispatch(resetAuthAction());

    resetRoutes();

    cacheTabs();

    if (!previousRoute?.handle?.constant) {
      if (previousRoute?.fullPath) {
        push('/login', { redirect: previousRoute.fullPath }, null, true);
      } else {
        navigate('/login', { replace: true });
      }
    }
  }

  return resetAuth;
}
